/**
 * I1 — Type Mask Reveal (Unseen Studio 톤)
 *
 * 단어 단위 split → clip-path (또는 transform) 위→아래 reveal.
 * STAGGER.base · DUR.slower · EASE.outExpo.
 *
 * 트리거: <h1 data-anim="type-mask">...</h1>
 *   • SplitCharsService.split 으로 .word / .char 분해 (기존 서비스 재사용)
 *   • IntersectionObserver 진입 시 1회 발동
 *   • prefers-reduced-motion 시 즉시 최종 상태
 *
 * 가이드: plan §2.1 / §7.1 A-2a
 */

import { SplitCharsService } from "./SplitCharsService";
import { DUR, EASE, STAGGER, motionOK } from "./MotionTokens";

declare global {
    interface Window {
        gsap?: {
            set: (target: unknown, vars: object) => unknown;
            to: (target: unknown, vars: object) => unknown;
            fromTo: (target: unknown, from: object, to: object) => unknown;
        };
    }
}

export class TypeMaskRevealController {
    private static observer: IntersectionObserver | null = null;

    static init(): void {
        const targets = Array.from(
            document.querySelectorAll<HTMLElement>('[data-anim="type-mask"]'),
        );
        if (!targets.length) return;

        // 모든 타겟을 split (단어/글자 단위)
        targets.forEach((el) => {
            if (!el.dataset.splitDone) {
                SplitCharsService.split(el);
            }
            // 초기 상태: word 단위 mask (아래로 숨김)
            TypeMaskRevealController.applyInitialState(el);
        });

        // motionOK = false (prefers-reduced-motion) → 즉시 최종 상태
        if (!motionOK()) {
            targets.forEach((el) => TypeMaskRevealController.snapToEnd(el));
            return;
        }

        const gsap = window.gsap;
        if (!gsap) {
            // GSAP 없으면 graceful: CSS class 토글로 처리
            targets.forEach((el) => TypeMaskRevealController.snapToEnd(el));
            return;
        }

        // IntersectionObserver 로 진입 감지 → 1회 발동
        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        TypeMaskRevealController.play(entry.target as HTMLElement);
                        this.observer?.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
        );

        targets.forEach((el) => this.observer!.observe(el));
    }

    /** 초기 상태: 각 word 를 overflow:hidden 컨테이너로 감싸지 않고,
     *  word 자체에 transform 으로 아래로 숨김 + clip-path 로 잘라냄 */
    private static applyInitialState(el: HTMLElement): void {
        const words = el.querySelectorAll<HTMLElement>(".word");
        words.forEach((word) => {
            word.style.display = "inline-block";
            word.style.transform = "translateY(100%)";
            word.style.opacity = "0";
            word.style.willChange = "transform, opacity";
        });
        // 부모 컨테이너에 clip-path 마스크 (단어가 컨테이너 밖으로 안 보임)
        el.style.overflow = "hidden";
        el.style.lineHeight = el.style.lineHeight || "1.1";
    }

    /** GSAP 으로 stagger reveal */
    private static play(el: HTMLElement): void {
        const gsap = window.gsap;
        if (!gsap) return;
        const words = el.querySelectorAll<HTMLElement>(".word");
        if (!words.length) return;

        gsap.to(words, {
            y: 0,
            opacity: 1,
            duration: DUR.slower,
            ease: EASE.outExpo,
            stagger: STAGGER.base,
            onComplete: () => {
                // willChange 해제 (GPU 자원 반납)
                words.forEach((w) => {
                    w.style.willChange = "";
                });
            },
        });
    }

    /** reduced-motion 또는 GSAP 부재 시: 즉시 최종 상태 */
    private static snapToEnd(el: HTMLElement): void {
        const words = el.querySelectorAll<HTMLElement>(".word");
        words.forEach((w) => {
            w.style.transform = "none";
            w.style.opacity = "1";
            w.style.willChange = "";
        });
    }
}
